import Head from "next/head";
import CLMHero2 from "components/CLM/CLMHero2";
import CTASection from "components/CLM/CTASection";
import EqualDivisionSection from "components/CLM/EqualDivisionSection";
import FAQ2 from "components/CLM/FAQ2";
import { CLMButtonPrimary } from "components/CLM/v3/Buttons";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { useInView } from "framer-motion";
import { BASE_ASSETS_URL } from "@constants/constants";

const CLMHeroData = {
  // title: "Reporting",
  heading: "Create and review contracts - exactly how you want!",
  description:
    "Edit contracts directly in Word and collaborate in real time—streamlining your workflow and speeding up approvals.",
  image: `${BASE_ASSETS_URL}/v2/clm/clm/hero-img-contract-editor-1.png`,
  btnText: "Review a Contract for Free",
  btnUrl: "/clm/request-demo",
};

const customerServiceFaqData = [
  {
    question: "What is BoloSign’s Microsoft Word integration?",
    answer:
      "BoloSign’s Microsoft Word integration transforms Word into a full-featured contract editing and collaboration environment. Legal and business teams can create, review, and manage contracts within Word, while all updates sync automatically with BoloSign’s Contract Lifecycle Management (CLM) system.",
  },
  {
    question:
      "How does BoloSign upgrade Microsoft Word for contract workflows?",
    answer:
      "BoloSign offers a dedicated Word Add-In tailored for contract management. It includes features such as real-time collaboration, automated version tracking, and AI-powered contract insights. These tools streamline workflows, enhance accuracy, and improve team productivity.",
  },
  {
    question: "Can I make edits and redlines in Word using BoloSign?",
    answer:
      "Yes. BoloSign allows you to edit, redline, and format contracts directly in Microsoft Word. All changes are automatically saved and tracked, with version control ensuring a complete history of revisions.",
  },
  {
    question: "How does version control work with BoloSign in Word?",
    answer:
      "BoloSign continuously saves drafts and creates new versions at key stages of the contract process. This ensures every update is recorded, allowing users to view and compare changes with a clear, chronological history.",
  },
  {
    question:
      "How does live collaboration function within Word using BoloSign?",
    answer:
      "BoloSign enables multiple users to collaborate on the same document in real time within Word. Users can view who is editing specific sections and control comment visibility to keep internal discussions private.",
  },
  {
    question: "Can I manage comments and feedback inside Word with BoloSign?",
    answer:
      "Yes. BoloSign supports organizing and managing comments directly in Word. You can easily address feedback from different collaborators and set visibility rules to ensure sensitive notes remain internal.",
  },
  {
    question:
      "What is AI Smart Review, and how does it assist with contracts in Word?",
    answer:
      "AI Smart Review is BoloSign’s AI-powered assistant integrated into Word. It helps streamline contract reviews by offering intelligent suggestions, applying redlines, and checking clauses against legal playbooks—empowering faster, more accurate first-pass reviews.",
  },
];

const Editor = ({ isDarkMode, setIsDarkMode }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const topTrigger = 120; // or some margin like 100
      const bottomTrigger = window.innerHeight;

      const isInView =
        rect.top <= topTrigger && rect.bottom >= bottomTrigger / 2;

      setIsDarkMode(isInView);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Head>
        <title>
          Contract Editor Software | BoloSign CLM Contract Management
        </title>
        <meta
          name="description"
          content="BoloSign’s contract editor lets legal teams draft, review, and collaborate on contracts directly in Microsoft Word—streamlining workflows and speeding up approvals."
        />
        <meta
          name="keywords"
          content="Contract editor software, contract drafting tools, Microsoft Word integration, contract collaboration, legal tech, BoloSign contract editor, CLM software"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/editor/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />

      <section className="text-black pb-12 pt-20 md:pt-32 px-6 md:px-8 bg-cream">
        <div className="max-w-screen-xl xl:px-8 mx-auto flex flex-col gap-6 justify-between items-center text-start md:text-center">
          <h2 className="text-3xl font-semibold md:font-bold md:text-5xl">
            Draft, review & edit without limits
          </h2>
          <p className="text-xl md:text-2xl xl:w-[65%] md:w-[80%] mx-auto">
            Collaborate in real time, edit without constraints, and redline
            directly within your workspace — no more tool-switching.
          </p>
          <div className="flex flex-row md:gap-3 items-end mt-6">
            <div className="flex-1">
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/clm/editor-draft-one.png`}
                alt="contract-editor"
                width={280}
                height={313}
                className=" hidden md:flex flex-2"
              />
            </div>
            <div className="flex-2">
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/clm/editor-draft-middle.png`}
                alt="contract-editor"
                width={630}
                height={400}
                className="md:block flex-1 w-[500px] xl:w-[630px]"
              />
            </div>
            <div className="flex-1">
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/clm/editor-draft-three.png`}
                alt="contract-editor"
                width={280}
                height={313}
                className=" hidden md:block flex-2"
              />
            </div>
          </div>
        </div>
      </section>

      <section className={`pt-7 md:py-16 md:pt-24 bg-cream`}>
        <div className="max-w-screen-xl mx-auto grid md:grid-cols-2 gap-6 md:gap-20 items-end">
          {/* <section className="text-black py-12 px-6 md:px-8 bg-cream">
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row gap-6 justify-between items-center text-center"> */}
          <div className="flex-1 flex flex-col gap-4 md:gap-8 items-end px-10 pl-8">
            <p className="text-xl md:text-2xl">
              Drafts are saved automatically, versions are logged, and your team
              always stays in sync.
            </p>
            <div className="flex-1">
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/clm/version-two.png`}
                alt="contract-editor"
                width={305}
                height={313}
                className=" md:flex h-full w-full"
              />
            </div>
          </div>
          <div className="flex-1 flex flex-col md:gap-16 gap-4 justify-between items-end px-10 pr-8 py-16 md:py-0">
            <p className="text-xl md:text-2xl">
              Eliminate drafting errors with reusable, pre-approved repository
              of approved clauses.
            </p>
            <div className="flex-1">
              <Image
                src={`${BASE_ASSETS_URL}/v2/clm/clm/clause-lib-1.png`}
                alt="contract-editor"
                width={305}
                height={313}
                className=" md:flex h-full w-full"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="text-start md:text-center">
        <h2 className="text-3xl font-semibold md:text-5xl mb-6 mx-auto mt-20 md:mt-32 md:max-w-[950px] px-6 md:px-0">
          Edit contracts in Word right inside BoloSign
        </h2>
        <p className="text-xl md:text-2xl md:w-[80%] xl:w-[50%] mx-auto mb-12 md:max-w-[950px] px-6 md:px-0">
          BoloSign lets you draft, edit, and review contracts using Microsoft
          Word’s interface—plus advanced tools built for legal teams.
        </p>

        <div
          className={`transition-colors duration-300  ${
            isDarkMode ? "bg-dark text-white" : "bg-white text-black"
          }`}
        >
          <section ref={sectionRef} className={`pt-20 `}>
            <EqualDivisionSection
              // title="Templates"
              heading="Seamless contract editing"
              description="If you are comfortable with Microsoft Word for editing, with BoloSign’s Word integration, your contract workflows stay smooth, familiar, and fully synced—no switching tools or losing context."
              image={`${BASE_ASSETS_URL}/v2/clm/clm/editor-employee-agreement.png`}
              // bgColor="bg-black"
              bgColor={isDarkMode ? "bg-dark" : "bg-transparent"}
              imageRight={false}
              checks={[
                "Edit directly in Word, save directly to BoloSign with formatting, comments, and structure stay perfectly preserved.",
                "Collaborate confidently. From edits and redlines to final approvals, everyone stays aligned.",
                "Instantly see who’s reviewing, editing, or commenting on each version inside BoloSign.",
              ]}
              // opinion="“This helps us show leadership how much we’re handling—and makes a strong case for expanding our team.”"
            />
          </section>
        </div>
      </div>

      <EqualDivisionSection
        // title="Templates"
        heading="Real-time collaboration"
        description="For fast-moving teams working together on contracts, our editor enables seamless co-authoring and communication."
        image={`${BASE_ASSETS_URL}/v2/clm/clm/editor-collaboration.png`}
        bgColor="bg-cream"
        imageRight={true}
        checks={[
          "Collaborate live with your team. Multiple users can edit the same contract at once, with instant updates reflected for everyone.",
          "Comment smartly. Mention teammates in real-time, separate internal vs external notes, and keep feedback flowing smoothly.",
          "Never lose progress. Drafts are automatically saved as you work, and you stay in control of when to publish official versions.",
        ]}
        // opinion="“This helps us show leadership how much we’re handling—and makes a strong case for expanding our team.”"
      />

      <EqualDivisionSection
        // title="Templates"
        heading="Review contracts with AI"
        description="AI Verification brings speed and precision to your contract workflow by letting you automate reviews with intelligent, customizable playbooks—powered by AI trained on expert legal insight."
        // image={`${BASE_ASSETS_URL}/v2/clm/clm/editor-review.gif")}
        video={`${BASE_ASSETS_URL}/v2/clm/videos/editor-review.mp4`}
        bgColor="bg-transparent"
        imageRight={false}
        checks={[
          "Create and customize contract review playbooks in seconds.",
          "Check contracts for compliance with internal and external policies automatically.",
          "Receive clear suggestions, redlines, and comments with AI-level accuracy.",
          // "Empower your team to handle initial reviews independently and confidently",
        ]}
        // opinion="“This helps us show leadership how much we’re handling—and makes a strong case for expanding our team.”"
      />
      {/* </div> */}
      <section className="bg-[#f4f2f0] text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={customerServiceFaqData} heading={"FAQs"} />
      </section>
    </>
  );
};

export default Editor;
